import {
  boxToPoints,
  isPointInPolygon,
  getReorientedPanel,
  foreshortenIsomorphic,
} from "@logic/lib/2d"
import { studioToScreen } from "@logic/lib/design"

import type { TransactionOperation } from "~/containers/LegacyDesigner/state/elements/transactions"

import { areaIndex, findAreaState, projectFace } from ".."
import { findCenterOfPanels } from "./findCenterOfPanels"
import { getPanelFootprint } from "./getPanelFootprint"
import { zoomState } from "../../view"
import { findPanelState, panelIndex } from "../panels"
import { areasOfPanelsIndexState } from "../relations"
import type { UnknownChangeForUnknownElement } from "../timeline"
import { divergeTimeline, generateElementChange } from "../timeline/transactions"

export const revisitOutline: TransactionOperation = (transactors) => {
  const { get, set } = transactors
  const changes: UnknownChangeForUnknownElement[] = []
  const panelsIds = get(panelIndex)
  const displacement = { x: 0, y: 0 }
  const shouldSnap = { x: false, y: false }
  const offset = { x: 0, y: 0 }
  const areaIds = get(areaIndex)

  for (const areaId of areaIds) {
    const panelsOfAreaId = []
    const zoom = get(zoomState)
    const area = projectFace(get, areaId, findAreaState)
    for (const panelId of panelsIds) {
      const panelState = findPanelState(panelId)
      const panel = get(panelState)

      const areaFullyContainsPanel = boxToPoints(
        studioToScreen.panel(panel, zoom),
      ).every((point) => isPointInPolygon(point, area))

      if (areaFullyContainsPanel) {
        panelsOfAreaId.push(panelId)
        set(areasOfPanelsIndexState, (current) => current.set(areaId, panelId))
      }
    }
    const rotatedOrigin = findCenterOfPanels(transactors, panelsOfAreaId)
    panelsOfAreaId.forEach((panelId) => {
      const panelState = findPanelState(panelId)
      const panel = get(panelState)
      const azimuth = Math.round(area.azimuth ?? 0)
      const { slope } = area
      const rotatedPanel = getReorientedPanel({
        panel,
        origin: rotatedOrigin,
        azimuth,
        addedRotation: 0,
      })
      const isLandscape =
        rotatedPanel.orientation === 90 || rotatedPanel.orientation === 270

      const moduleScale = getPanelFootprint(transactors, panel)
      const moduleScaleInPerspective = foreshortenIsomorphic(
        slope,
        isLandscape ? `x` : `y`,
      ).box({ ...rotatedPanel, ...moduleScale }, rotatedOrigin)

      set(panelState, {
        ...rotatedPanel,
        ...moduleScaleInPerspective,
        x: rotatedPanel.x + displacement.x,
        y: rotatedPanel.y + displacement.y,
        xSnap: shouldSnap.x ? rotatedPanel.x + offset.x : null,
        ySnap: shouldSnap.y ? rotatedPanel.y + offset.y : null,
      })

      changes.push(
        generateElementChange(transactors, {
          action: {
            type: `UPDATE`,
            family: `PANEL`,
            id: panelId,
            payload: rotatedPanel,
          },
        }),
      )
    })

    divergeTimeline(transactors, changes)
  }
}
